/**
 * DocsAPI-family office provider resolver (#617, EuroOffice amendment §10).
 *
 * OnlyOffice and its EuroOffice fork speak the SAME protocol — `DocsAPI.DocEditor`,
 * numeric save callbacks (2/4/6), `CommandService.ashx`/`forcesave`, `refreshFile` — so
 * ONE adapter serves both: OnlyOfficeEditor / OnlyOfficeModifier / OnlyOfficeDocViewer on
 * the client and the /api/wopi callback+forcesave state machine on the server (gated by
 * IS_DOCSAPI there). Only three things differ per provider: the api.js base URL, the
 * editor-registry id prefix, and the brand label. Everything else — token mint, the
 * dirty/draft/discard/conflict/pointer-re-key logic — is shared verbatim; do NOT fork a
 * second modifier or a second callback state machine to change those three strings.
 *
 * A deployment runs exactly ONE office engine (VITE_OFFICE_PROVIDER); this resolves which
 * DocsAPI provider is active. Collabora is a different engine entirely (not DocsAPI).
 */
export type DocsApiProviderKey = 'onlyoffice' | 'eurooffice'
type ViteEnv = Record<string, string | undefined>
const env = ((import.meta as any).env ?? {}) as ViteEnv

export const OFFICE_PROVIDER = (env.VITE_OFFICE_PROVIDER || 'collabora') as 'collabora' | DocsApiProviderKey

/** True when the active office engine is a DocsAPI-family provider (OnlyOffice or EuroOffice). */
export const IS_DOCSAPI_PROVIDER = OFFICE_PROVIDER === 'onlyoffice' || OFFICE_PROVIDER === 'eurooffice'

export interface DocsApiProvider {
  key: DocsApiProviderKey
  /** Document-server base; api.js loads from `${baseUrl}/web-apps/apps/api/documents/api.js`. */
  baseUrl: string
  /** Editor-registry id prefix → `${idPrefix}-{sheet,doc,slide}` (keeps preferredEditors meaningful). */
  idPrefix: DocsApiProviderKey
  /** Brand label for editor names. */
  label: string
}

/**
 * The active DocsAPI provider descriptor. EuroOffice when VITE_OFFICE_PROVIDER=eurooffice,
 * else OnlyOffice (the validated baseline / dev comparison target). Never describes
 * Collabora — call IS_DOCSAPI_PROVIDER first.
 */
// Resolve the document-server base. In DEV, default to the local container. In a
// PRODUCTION build, do NOT silently fall back to localhost: a public page loading the
// editor from localhost trips Chrome's "access other apps and services on this device"
// (Local Network Access) prompt AND the editor never loads. Fail LOUD instead so a
// missing CF Pages build var (e.g. VITE_EUROOFFICE_URL) is obvious, not a scary prompt.
// (stagetest, 2026-09-11: VITE_OFFICE_PROVIDER=eurooffice set, VITE_EUROOFFICE_URL not.)
function resolveBaseUrl(envUrl: string | undefined, key: DocsApiProviderKey): string {
  if (envUrl) return envUrl
  if ((import.meta as any).env?.PROD) {
    console.error(`[office] VITE_${key.toUpperCase()}_URL is not set — the ${key} editor cannot load. Set it on the CF Pages project's build env.`)
    return ''
  }
  // Dev: `localhost` (same-site with the SPA on localhost:7474, so the editor
  // iframe's storage isn't partitioned). The docserver must answer localhost:9090
  // over BOTH IPv4 and IPv6 for this to work in Chrome/Firefox (they try ::1
  // first) — handled by the ::1 loopback bind in docker-compose.override.yml
  // (#633). Prod sets VITE_ONLYOFFICE_URL (handled above); this is dev-only.
  return 'http://localhost:9090'
}

export function docsApiProvider(): DocsApiProvider {
  if (OFFICE_PROVIDER === 'eurooffice') {
    return {
      key: 'eurooffice',
      baseUrl: resolveBaseUrl(env.VITE_EUROOFFICE_URL, 'eurooffice'),
      idPrefix: 'eurooffice',
      label: 'EuroOffice',
    }
  }
  return {
    key: 'onlyoffice',
    baseUrl: resolveBaseUrl(env.VITE_ONLYOFFICE_URL, 'onlyoffice'),
    idPrefix: 'onlyoffice',
    label: 'OnlyOffice',
  }
}
